import React, { useState, useRef } from 'react';
import { Upload, Loader2 } from 'lucide-react';

const ASSET_TYPES = [
  { value: 'graphic', label: 'Graphic' },
  { value: 'social',  label: 'Social Media' },
  { value: 'slide',   label: 'Slide' },
  { value: 'outline', label: 'Outline' },
] as const;

type AssetType = typeof ASSET_TYPES[number]['value'];

interface Props {
  seriesId: string;
  sermonId: string | null;
  onUpload: (seriesId: string, sermonId: string | null, file: File, label: string, assetType: AssetType) => Promise<void>;
}

export function AssetUploaderPanel({ seriesId, sermonId, onUpload }: Props) {
  const [label, setLabel] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('graphic');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    setFile(f);
    if (!label) setLabel(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file || !label.trim()) { setError('File and label are required'); return; }
    setUploading(true);
    setError(null);
    setSuccess(false);
    try {
      await onUpload(seriesId, sermonId, file, label.trim(), assetType);
      setFile(null);
      setLabel('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg py-5 cursor-pointer transition-colors ${
          dragging ? 'border-electric bg-electric/5' : 'border-sand/40 dark:border-slate/30 hover:border-electric/50'
        }`}
      >
        <Upload className={`h-5 w-5 ${dragging ? 'text-electric' : 'text-slate/50'}`} />
        <p className="text-xs text-slate dark:text-soft-white/50">
          {file ? file.name : 'Drop file here or click to browse'}
        </p>
        <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-slate dark:text-soft-white/60 mb-1">Label</label>
          <input value={label} onChange={e => setLabel(e.target.value)}
            className="w-full px-2.5 py-1.5 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
            placeholder="e.g. Week 1 Graphic" />
        </div>
        <div>
          <label className="block text-xs text-slate dark:text-soft-white/60 mb-1">Type</label>
          <select value={assetType} onChange={e => setAssetType(e.target.value as AssetType)}
            className="w-full px-2.5 py-1.5 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white">
            {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
      {success && <p className="text-xs text-emerald-600">Uploaded successfully.</p>}

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="flex items-center gap-2 px-3 py-1.5 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 disabled:opacity-40"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        Upload Asset
      </button>
    </div>
  );
}
